/**
 * EventQueue - sessionStorageベースのイベントキュー
 *
 * 未送信イベントをsessionStorageに保存し、バッチ送信を管理
 */

import type { TrackedEvent } from '@common/models';
import type { EventQueue as EventQueueData } from './types';

const STORAGE_KEY = 'talkn_event_queue';
const MAX_QUEUE_SIZE = 100; // 最大キューサイズ

export class EventQueue {
  private storageKey: string;

  constructor(storageKey: string = STORAGE_KEY) {
    this.storageKey = storageKey;
  }

  /**
   * キューにイベントを追加
   * @param event - 追加するイベント
   */
  enqueue(event: TrackedEvent): void {
    const queue = this.getQueue();

    // イベントを追加
    queue.events.push(event);

    // 最大サイズ制限（古いイベントを削除）
    if (queue.events.length > MAX_QUEUE_SIZE) {
      queue.events = queue.events.slice(-MAX_QUEUE_SIZE);
    }

    queue.lastSyncTimestamp = Date.now();
    this.saveQueue(queue);
  }

  /**
   * 複数イベントをバッチで追加
   * @param events - イベント配列
   */
  enqueueBatch(events: TrackedEvent[]): void {
    const queue = this.getQueue();

    queue.events.push(...events);

    // 最大サイズ制限
    if (queue.events.length > MAX_QUEUE_SIZE) {
      queue.events = queue.events.slice(-MAX_QUEUE_SIZE);
    }

    queue.lastSyncTimestamp = Date.now();
    this.saveQueue(queue);
  }

  /**
   * キューから指定数のイベントを取り出す（削除しない）
   * @param count - 取り出す数
   * @returns イベント配列
   */
  peek(count: number): TrackedEvent[] {
    const queue = this.getQueue();
    return queue.events.slice(0, count);
  }

  /**
   * キューから指定数のイベントを削除
   * @param count - 削除する数
   */
  dequeue(count: number): void {
    const queue = this.getQueue();
    queue.events = queue.events.slice(count);
    queue.lastSyncTimestamp = Date.now();
    this.saveQueue(queue);
  }

  /**
   * キューサイズ取得
   * @returns キュー内のイベント数
   */
  size(): number {
    return this.getQueue().events.length;
  }

  /**
   * キューが空かどうか
   * @returns 空ならtrue
   */
  isEmpty(): boolean {
    return this.size() === 0;
  }

  /**
   * キューをクリア
   */
  clear(): void {
    this.saveQueue({
      events: [],
      lastSyncTimestamp: Date.now(),
    });
  }

  /**
   * 最終同期タイムスタンプ取得
   * @returns Unix timestamp (ms)
   */
  getLastSyncTimestamp(): number {
    return this.getQueue().lastSyncTimestamp;
  }

  /**
   * sessionStorageからキューを取得
   * @returns キューデータ
   */
  private getQueue(): EventQueueData {
    try {
      const data = sessionStorage.getItem(this.storageKey);
      if (!data) {
        return {
          events: [],
          lastSyncTimestamp: Date.now(),
        };
      }

      const parsed = JSON.parse(data) as EventQueueData;
      return {
        events: Array.isArray(parsed.events) ? parsed.events : [],
        lastSyncTimestamp: parsed.lastSyncTimestamp || Date.now(),
      };
    } catch (error) {
      console.error('[EventQueue] Failed to parse queue:', error);
      return {
        events: [],
        lastSyncTimestamp: Date.now(),
      };
    }
  }

  /**
   * sessionStorageにキューを保存
   * @param queue - キューデータ
   */
  private saveQueue(queue: EventQueueData): void {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(queue));
    } catch (error) {
      console.error('[EventQueue] Failed to save queue:', error);

      // ストレージ容量エラーの場合、古いイベントを削除して再試行
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.warn('[EventQueue] Storage quota exceeded, removing old events');
        queue.events = queue.events.slice(-50); // 最新50件のみ保持

        try {
          sessionStorage.setItem(this.storageKey, JSON.stringify(queue));
        } catch (retryError) {
          console.error('[EventQueue] Failed to save queue after retry:', retryError);
        }
      }
    }
  }
}
